/**
 * Unico punto in cui i valori raw dei controller diventano grandezze fisiche.
 *
 * Usato in due momenti:
 *  - all'ingest, per popolare le colonne calibrate di `readings`;
 *  - dal ricalcolo storico, quando l'utente cambia una calibrazione dalla UI.
 *
 * Conseguenza di design: nessun firmware conosce percentuali o soglie calibrate.
 * Per il percorso LoRa, dove il nodo deve decidere da solo dentro la finestra RX,
 * la soglia viene riconvertita in raw con `moisturePctToRaw`.
 */
import { DEFAULT_LIPO_CURVE, clamp, } from './index.js';
/** 1 kPa di pressione idrostatica equivale a questa colonna d'acqua, in cm. */
const CM_H2O_PER_KPA = 10.19716;
/** Il registro di pressione dell'XGZP6847D e' a 24 bit in complemento a due. */
const PRESSURE_FULL_SCALE = 1 << 24;
const PRESSURE_SIGN_BIT = 1 << 23;
/** Converte il contenuto grezzo del registro a 24 bit in conteggi con segno. */
export function toSignedCounts(raw) {
    const v = Math.round(raw) & (PRESSURE_FULL_SCALE - 1);
    return v >= PRESSURE_SIGN_BIT ? v - PRESSURE_FULL_SCALE : v;
}
/** Interpolazione lineare su una curva di punti ordinati per mV. */
export function interpolateCurve(curve, mv) {
    if (curve.length === 0)
        return 0;
    const first = curve[0];
    const last = curve[curve.length - 1];
    if (mv <= first.mv)
        return first.pct;
    if (mv >= last.mv)
        return last.pct;
    for (let i = 1; i < curve.length; i++) {
        const hi = curve[i];
        if (mv <= hi.mv) {
            const lo = curve[i - 1];
            const span = hi.mv - lo.mv;
            if (span <= 0)
                return hi.pct;
            return lo.pct + ((mv - lo.mv) * (hi.pct - lo.pct)) / span;
        }
    }
    return last.pct;
}
export function calibrateBattery(kind, params, raw) {
    if (kind === 'lipo_mv') {
        // Il firmware CubeCell riporta gia' i millivolt: resta solo la curva.
        const p = params;
        const curve = p.curve?.length ? p.curve : DEFAULT_LIPO_CURVE;
        return { mv: raw, pct: clamp(interpolateCurve(curve, raw), 0, 100) };
    }
    if (kind === 'lipo_divider') {
        const p = params;
        const adcMax = p.adc_max || 4095;
        const ratio = p.r2_kohm > 0 ? (p.r1_kohm + p.r2_kohm) / p.r2_kohm : 1;
        const gain = p.gain || 1;
        const mv = (raw / adcMax) * p.vref * ratio * 1000 * gain + (p.offset_mv || 0);
        return { mv, pct: clamp(interpolateCurve(DEFAULT_LIPO_CURVE, mv), 0, 100) };
    }
    throw new Error(`Calibrazione batteria non supportata: ${kind}`);
}
/**
 * Sonda capacitiva v1.2: raw alto = terreno asciutto, quindi la mappa e'
 * invertita rispetto all'intuizione (raw_dry -> 0 %, raw_wet -> 100 %).
 */
export function calibrateMoisture(params, raw) {
    const p = params;
    const span = p.raw_wet - p.raw_dry;
    if (span === 0)
        return 0;
    // `+ 0` normalizza il -0 prodotto quando raw coincide con raw_dry.
    return clamp(((raw - p.raw_dry) / span) * 100, 0, 100) + 0;
}
/** Inversa di `calibrateMoisture`: serve per spedire la soglia al nodo LoRa in unita' raw. */
export function moisturePctToRaw(params, pct) {
    const p = params;
    return Math.round(p.raw_dry + (clamp(pct, 0, 100) / 100) * (p.raw_wet - p.raw_dry));
}
export function calibrateWaterLevel(params, raw) {
    const p = params;
    const k = p.k_factor || 1;
    const density = p.density || 1;
    const kpa = (toSignedCounts(raw) - (p.raw_offset || 0)) / k;
    const cm = (kpa * CM_H2O_PER_KPA) / density;
    const height = p.tank_height_cm || 1;
    return { kpa, cm, pct: clamp((cm / height) * 100, 0, 100) };
}
const EMPTY = {
    batteryMv: null,
    batteryPct: null,
    moisturePct: null,
    waterLevelCm: null,
    waterLevelPct: null,
};
/**
 * Un sensore senza calibrazione, o con parametri incoerenti, produce `null` sul
 * valore calibrato ma non fa fallire l'ingest: il raw resta comunque salvato e
 * la lettura diventa recuperabile appena la calibrazione viene sistemata.
 */
export function calibrateReading(raw, calibrations) {
    const out = { ...EMPTY };
    if (raw.batteryRaw != null) {
        const cal = calibrations.battery;
        if (cal) {
            try {
                const battery = calibrateBattery(cal.kind, cal.params, raw.batteryRaw);
                out.batteryMv = round(battery.mv, 1);
                out.batteryPct = round(battery.pct, 1);
            }
            catch {
                /* calibrazione incoerente: si conserva solo il raw */
            }
        }
    }
    if (raw.moistureRaw != null && calibrations.moisture) {
        out.moisturePct = round(calibrateMoisture(calibrations.moisture.params, raw.moistureRaw), 1);
    }
    if (raw.waterLevelRaw != null && calibrations.water_level) {
        const level = calibrateWaterLevel(calibrations.water_level.params, raw.waterLevelRaw);
        out.waterLevelCm = round(level.cm, 1);
        out.waterLevelPct = round(level.pct, 1);
    }
    return out;
}
function round(value, decimals) {
    const f = 10 ** decimals;
    return Math.round(value * f) / f;
}
//# sourceMappingURL=calibration.js.map